import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';

import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Collapse, Divider, IconButton, Stack } from '@mui/material';

import { useShallow } from 'zustand/react/shallow';

import { AdvertisingPackageResponse, SpaceResponse } from '@/api/data-contracts';
import useGeneratikTableStore from '@/shared/ui/GeneratikTable/generatikTableStore';

import { Title } from './ShoppingCart.styles';
import ShoppingCartPackageItem from './ShoppingCartPackageItem';
import ShoppingCartSpaceItem from './ShoppingCartSpaceItem';

interface Props {
  type: 'spaces' | 'packages';
  handleClose: () => void;
}

const ShoppingCartList: FC<Props> = ({ type, handleClose }) => {
  const { t } = useTranslation();

  const { selectedSpaces, selectedPackages } = useGeneratikTableStore(
    useShallow((state) => ({
      selectedSpaces: (state.selectedRows['ADVERTISING_SPACES'] ?? []) as SpaceResponse[],
      selectedPackages: (state.selectedRows['ADVERTISING_PACKAGES'] ??
        []) as AdvertisingPackageResponse[],
    })),
  );

  if (type === 'spaces' && selectedSpaces.length === 0) {
    return null;
  }

  if (type === 'packages' && selectedPackages.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Title>{t(type === 'spaces' ? 'Advertising Spaces' : 'Advertising Packages')}</Title>
        {(type === 'spaces' && selectedSpaces.length > 0) ||
        (type === 'packages' && selectedSpaces.length === 0) ? (
          <IconButton size="small" onClick={handleClose}>
            <CloseOutlinedIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        ) : null}
      </Stack>

      <Divider sx={{ margin: '0.375rem 0 !important' }} />

      {type === 'spaces' && (
        <TransitionGroup>
          {selectedSpaces.map((space) => (
            <Collapse key={space.id} timeout={350}>
              <ShoppingCartSpaceItem space={space} rowId={space.id} />
            </Collapse>
          ))}
        </TransitionGroup>
      )}

      {type === 'packages' && (
        <TransitionGroup style={{ gap: '0.5rem' }}>
          {selectedPackages.map((advertisingPackage) => (
            <Collapse key={advertisingPackage.id} timeout={350}>
              <ShoppingCartPackageItem
                advertisingPackage={advertisingPackage}
                rowId={advertisingPackage.id}
              />
            </Collapse>
          ))}
        </TransitionGroup>
      )}
    </Stack>
  );
};

export default ShoppingCartList;
